/**
 * Stage 6–7 advanced harmony / keys / independence repertoire.
 */

import type { HandId, MusicPiece, NoteEvent } from "./types.js";

function chordHits(
  midis: number[],
  beats: number[],
  duration: number,
  hand: HandId = "right",
): NoteEvent[] {
  const notes: NoteEvent[] = [];
  for (const beat of beats) {
    for (const midi of midis) {
      notes.push({ beat, duration, midi, velocity: 95, hand });
    }
  }
  return notes;
}

function sequence(
  midis: number[],
  beatStep: number,
  hand: HandId = "right",
  startBeat = 0,
): NoteEvent[] {
  return midis.map((midi, i) => ({
    beat: startBeat + i * beatStep,
    duration: beatStep,
    midi,
    velocity: 90,
    hand,
  }));
}

function piece(
  partial: Omit<MusicPiece, "notes"> & { notes: NoteEvent[] },
): MusicPiece {
  return partial;
}

function scaleAscDesc(
  startMidi: number,
  degrees: number[],
  beatStep = 0.5,
  hand: HandId = "right",
): NoteEvent[] {
  const notes: NoteEvent[] = [];
  let beat = 0;
  const up = degrees.map((d) => startMidi + d);
  const down = [...up].reverse().slice(1);
  for (const midi of [...up, ...down]) {
    notes.push({ beat, duration: beatStep, midi, velocity: 90, hand });
    beat += beatStep;
  }
  return notes;
}

export const ADVANCED_PIECES: MusicPiece[] = [
  /* —— Stage 6: extended harmony —— */
  piece({
    id: "chord-c7-dom",
    title: "C Dominant 7 (C–E–G–Bb)",
    category: "chord",
    bpm: 72,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 6,
    description: "Dominant seventh block hits.",
    notes: chordHits([60, 64, 67, 70], [0, 2, 4, 6], 1),
  }),
  piece({
    id: "chord-cmaj7",
    title: "C Major 7 (C–E–G–B)",
    category: "chord",
    bpm: 72,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 6,
    description: "Major seventh color.",
    notes: chordHits([60, 64, 67, 71], [0, 2, 4, 6], 1),
  }),
  piece({
    id: "chord-amin7",
    title: "A Minor 7 (A–C–E–G)",
    category: "chord",
    bpm: 72,
    timeSignature: "4/4",
    key: "A minor",
    hand: "right",
    stage: 6,
    description: "Minor seventh block hits.",
    notes: chordHits([69, 72, 76, 79], [0, 2, 4, 6], 1),
  }),
  piece({
    id: "chord-bdim",
    title: "B Diminished Triad",
    category: "chord",
    bpm: 70,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 6,
    description: "Diminished triad (B–D–F).",
    notes: chordHits([71, 74, 77], [0, 2, 4, 6], 1),
  }),
  piece({
    id: "chord-caug",
    title: "C Augmented Triad",
    category: "chord",
    bpm: 70,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 6,
    description: "Augmented triad (C–E–G#).",
    notes: chordHits([60, 64, 68], [0, 2, 4, 6], 1),
  }),
  piece({
    id: "chord-csus2",
    title: "C Sus2 (C–D–G)",
    category: "chord",
    bpm: 72,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 6,
    description: "Suspended 2nd color.",
    notes: chordHits([60, 62, 67], [0, 2, 4, 6], 1),
  }),
  piece({
    id: "chord-csus4",
    title: "C Sus4 (C–F–G)",
    category: "chord",
    bpm: 72,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 6,
    description: "Suspended 4th → resolve feel (blocks only).",
    notes: chordHits([60, 65, 67], [0, 2, 4], 1).concat(chordHits([60, 64, 67], [6], 2)),
  }),
  piece({
    id: "prog-ii-v-i-c",
    title: "ii–V–I in C (Dm–G–C)",
    category: "chord",
    bpm: 76,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 6,
    description: "Jazz/pop fundamental progression as blocks.",
    notes: [
      ...chordHits([62, 65, 69], [0], 2), // Dm
      ...chordHits([67, 71, 74], [2], 2), // G
      ...chordHits([60, 64, 67], [4], 4), // C
    ],
  }),
  piece({
    id: "chord-g-inv1",
    title: "G Major 1st Inversion",
    category: "chord",
    bpm: 72,
    timeSignature: "4/4",
    key: "G major",
    hand: "right",
    stage: 6,
    description: "G triad inv1 (B–D–G).",
    notes: chordHits([71, 74, 79], [0, 2, 4, 6], 1),
  }),
  piece({
    id: "chord-f-inv1",
    title: "F Major 1st Inversion",
    category: "chord",
    bpm: 72,
    timeSignature: "4/4",
    key: "F major",
    hand: "right",
    stage: 6,
    description: "F triad inv1 (A–C–F).",
    notes: chordHits([69, 72, 77], [0, 2, 4, 6], 1),
  }),
  piece({
    id: "chord-d-inv1",
    title: "D Major 1st Inversion",
    category: "chord",
    bpm: 72,
    timeSignature: "4/4",
    key: "D major",
    hand: "right",
    stage: 6,
    description: "D triad inv1 (F#–A–D).",
    notes: chordHits([66, 69, 74], [0, 2, 4, 6], 1),
  }),
  piece({
    id: "circle-fifths-triads",
    title: "Circle of Fifths (Triad March)",
    category: "chord",
    bpm: 80,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 6,
    description: "C→G→D→A→E major triad hits (excerpt).",
    notes: [
      ...chordHits([60, 64, 67], [0], 2),
      ...chordHits([67, 71, 74], [2], 2),
      ...chordHits([62, 66, 69], [4], 2),
      ...chordHits([69, 73, 76], [6], 2),
      ...chordHits([64, 68, 71], [8], 2),
    ],
  }),
  piece({
    id: "lh-prog-i-v-vi-iv",
    title: "LH Pop Progression I–V–vi–IV",
    category: "exercise",
    bpm: 84,
    timeSignature: "4/4",
    key: "C major",
    hand: "left",
    stage: 6,
    description: "LH roots: C–G–A–F march.",
    notes: sequence([48, 48, 55, 55, 57, 57, 53, 53], 1, "left"),
  }),
  piece({
    id: "voicing-shell-c7",
    title: "Shell Voicing C7 (E–Bb)",
    category: "chord",
    bpm: 70,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 6,
    description: "Guide-tone shell (3rd + 7th) — voice-leading intro.",
    notes: chordHits([64, 70], [0, 2, 4, 6], 1),
  }),

  /* —— Stage 7: keys, etudes, independence, ear —— */
  piece({
    id: "scale-bb-major-rh",
    title: "Bb Major Scale (RH)",
    category: "scale",
    bpm: 76,
    timeSignature: "4/4",
    key: "Bb major",
    hand: "right",
    stage: 7,
    description: "Bb major 1 octave (Bb–C–D–Eb–F–G–A–Bb).",
    notes: scaleAscDesc(58, [0, 2, 4, 5, 7, 9, 11, 12], 0.5),
  }),
  piece({
    id: "scale-e-major-rh",
    title: "E Major Scale (RH)",
    category: "scale",
    bpm: 76,
    timeSignature: "4/4",
    key: "E major",
    hand: "right",
    stage: 7,
    description: "E major 1 octave.",
    notes: scaleAscDesc(64, [0, 2, 4, 5, 7, 9, 11, 12], 0.5),
  }),
  piece({
    id: "scale-a-major-rh",
    title: "A Major Scale (RH)",
    category: "scale",
    bpm: 76,
    timeSignature: "4/4",
    key: "A major",
    hand: "right",
    stage: 7,
    description: "A major 1 octave.",
    notes: scaleAscDesc(69, [0, 2, 4, 5, 7, 9, 11, 12], 0.5),
  }),
  piece({
    id: "etude-c-ascending-break",
    title: "Mini Etude: Ascending Breaks (C)",
    category: "exercise",
    bpm: 88,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 7,
    description: "Longer phrasing drill (~2 bars patterns).",
    notes: sequence(
      [60, 64, 67, 72, 67, 64, 60, 62, 65, 69, 72, 69, 65, 62, 60, 64, 67, 71, 72, 67, 64, 60],
      0.5,
    ),
  }),
  piece({
    id: "both-held-bass-independence",
    title: "Held Bass Independence",
    category: "exercise",
    bpm: 72,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 7,
    description: "LH C on strong beats while RH moves (re-strike LH each chord change).",
    notes: [
      { beat: 0, duration: 2, midi: 48, velocity: 80, hand: "left" },
      { beat: 0, duration: 1, midi: 64, velocity: 92, hand: "right" },
      { beat: 1, duration: 1, midi: 65, velocity: 92, hand: "right" },
      { beat: 2, duration: 2, midi: 48, velocity: 80, hand: "left" },
      { beat: 2, duration: 1, midi: 67, velocity: 92, hand: "right" },
      { beat: 3, duration: 1, midi: 65, velocity: 92, hand: "right" },
      { beat: 4, duration: 2, midi: 55, velocity: 80, hand: "left" },
      { beat: 4, duration: 1, midi: 64, velocity: 92, hand: "right" },
      { beat: 5, duration: 1, midi: 62, velocity: 92, hand: "right" },
      { beat: 6, duration: 2, midi: 48, velocity: 85, hand: "left" },
      { beat: 6, duration: 2, midi: 60, velocity: 95, hand: "right" },
    ],
  }),
  piece({
    id: "ear-echo-steps-c",
    title: "Ear Echo: Step Patterns in C",
    category: "exercise",
    bpm: 70,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 7,
    description: "Call→response patterns (play exactly — treat as memory dictation).",
    notes: sequence([60, 62, 64, 60, 64, 65, 67, 64, 60, 62, 60], 1),
  }),
  piece({
    id: "rhythm-perfect-quarters-locked",
    title: "Timing Drill: Locked Quarters",
    category: "exercise",
    bpm: 90,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 7,
    description: "Use with metronome lock — perfect quarter placement goal.",
    notes: sequence([60, 60, 60, 60, 62, 62, 62, 62, 64, 64, 64, 64, 60, 60, 60, 60], 1),
  }),
  piece({
    id: "dynamics-soft-loud-c",
    title: "Dynamics Seed: Soft→Loud Blocks",
    category: "chord",
    bpm: 66,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 7,
    description: "Same triad with soft then loud velocity targets (pitch still graded).",
    notes: [
      { beat: 0, duration: 2, midi: 60, velocity: 50, hand: "right" },
      { beat: 0, duration: 2, midi: 64, velocity: 50, hand: "right" },
      { beat: 0, duration: 2, midi: 67, velocity: 50, hand: "right" },
      { beat: 2, duration: 2, midi: 60, velocity: 110, hand: "right" },
      { beat: 2, duration: 2, midi: 64, velocity: 110, hand: "right" },
      { beat: 2, duration: 2, midi: 67, velocity: 110, hand: "right" },
      { beat: 4, duration: 2, midi: 60, velocity: 50, hand: "right" },
      { beat: 4, duration: 2, midi: 64, velocity: 50, hand: "right" },
      { beat: 4, duration: 2, midi: 67, velocity: 50, hand: "right" },
      { beat: 6, duration: 2, midi: 60, velocity: 110, hand: "right" },
      { beat: 6, duration: 2, midi: 64, velocity: 110, hand: "right" },
      { beat: 6, duration: 2, midi: 67, velocity: 110, hand: "right" },
    ],
  }),
  piece({
    id: "pedal-sustain-cue-c",
    title: "Pedal Cue Piece (Sustain Mindset)",
    category: "melody",
    bpm: 72,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 7,
    description: "Hold MIDI sustain (CC64) after each attack; clear before the next long tone.",
    skills: ["sustain pedal", "clean changes"],
    pedalCues: [
      { beat: 0.15, down: true },
      { beat: 4, down: false },
      { beat: 4.15, down: true },
      { beat: 8, down: false },
      { beat: 8.15, down: true },
      { beat: 12, down: false },
      { beat: 12.15, down: true },
      { beat: 16, down: false },
    ],
    notes: [
      { beat: 0, duration: 4, midi: 60, velocity: 88, hand: "right" },
      { beat: 4, duration: 4, midi: 64, velocity: 88, hand: "right" },
      { beat: 8, duration: 4, midi: 67, velocity: 88, hand: "right" },
      { beat: 12, duration: 4, midi: 72, velocity: 90, hand: "right" },
    ],
  }),
];
